import {
  ArrowRightOutlined, FacebookOutlined, GlobalOutlined, InstagramOutlined, NotificationOutlined, ShopOutlined,
  TeamOutlined, ThunderboltOutlined, TrophyOutlined, VideoCameraOutlined
} from '@ant-design/icons';
import { Col, Empty, Progress, Row, Skeleton, Tag, Tooltip, Typography } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useMemo } from 'react';
import { SourceCount } from '../../../types/dashboard';
import { glassCardStyle } from '../../Performance/utils';

const { Title, Text } = Typography;

interface LeadsBySourceCardProps {
  statsData: any;
  leadSources: any[];
  navigate: any;
  message: any;
  loading: boolean;
}

// Helper untuk ikon platform berdasarkan nama sumber promosi
const getPlatformIcon = (sourceName: string) => {
  const lower = (sourceName || '').toLowerCase();
  if (lower.includes('facebook') || lower.includes('fb') || lower.includes('meta')) {
    return <FacebookOutlined style={{ color: '#1877f2', fontSize: '15px' }} />;
  }
  if (lower.includes('instagram') || lower.includes('ig')) {
    return <InstagramOutlined style={{ color: '#e1306c', fontSize: '15px' }} />;
  }
  if (lower.includes('tiktok')) {
    return <VideoCameraOutlined style={{ color: '#0f172a', fontSize: '15px' }} />;
  }
  if (
    lower.includes('google') ||
    lower.includes('web') ||
    lower.includes('website') ||
    lower.includes('portal')
  ) {
    return <GlobalOutlined style={{ color: '#4285f4', fontSize: '15px' }} />;
  }
  if (
    lower.includes('walk') ||
    lower.includes('spanduk') ||
    lower.includes('baliho') ||
    lower.includes('pameran') ||
    lower.includes('event')
  ) {
    return <ShopOutlined style={{ color: '#d97706', fontSize: '15px' }} />;
  }
  if (
    lower.includes('referral') ||
    lower.includes('rekomendasi') ||
    lower.includes('agent') ||
    lower.includes('agen')
  ) {
    return <TeamOutlined style={{ color: '#10b981', fontSize: '15px' }} />;
  }
  return <NotificationOutlined style={{ color: '#7BAA18', fontSize: '15px' }} />;
};

export const LeadsBySourceCard: React.FC<LeadsBySourceCardProps> = ({
  statsData,
  leadSources,
  navigate,
  message,
  loading,
}) => {
  const isCardLoading = loading || !statsData;
  const rawSources: SourceCount[] = useMemo(() => {
    return statsData?.leads_by_source || [];
  }, [statsData?.leads_by_source]);

  // Hitung total leads dari seluruh sumber
  const totalLeads = useMemo(() => {
    return rawSources.reduce((acc, curr) => acc + (curr.count || 0), 0);
  }, [rawSources]);

  // Urutkan media berdasarkan jumlah leads terbanyak ke terendah
  const sortedList = useMemo(() => {
    return [...rawSources].sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [rawSources]);

  // Lead count terbesar untuk perhitungan bar relatif
  const maxCount = useMemo(() => {
    return sortedList[0]?.count || 1;
  }, [sortedList]);

  // Handler klik pada sumber promosi
  const handleSourceClick = useMemo(() => {
    return (item: SourceCount) => {
      let sourceId = item.source_id;
      if (!sourceId && leadSources && leadSources.length > 0) {
        const match = leadSources.find(
          (s: any) => s.name?.toLowerCase() === item.source?.toLowerCase() || s.id === item.source
        );
        if (match) sourceId = match.id;
      }

      if (sourceId) {
        message.info(`Menyaring data leads berdasarkan sumber: ${item.source}`);
        navigate(`/leads/data?source=${sourceId}`);
      } else {
        message.info(`Menyaring data leads berdasarkan sumber: ${item.source}`);
        navigate(`/leads/data?search=${encodeURIComponent(item.source)}`);
      }
    };
  }, [leadSources, navigate, message]);

  // Opsi Donut Chart (Sisi Kiri - 2/3 width)
  const donutOptions = useMemo(() => {
    if (!sortedList || sortedList.length === 0) return null;

    const chartData = sortedList.map((item) => ({
      name: item.source,
      y: item.count,
      sourceItem: item,
    }));

    return {
      chart: {
        type: 'pie',
        height: 380,
        backgroundColor: 'transparent',
        style: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        },
      },
      title: {
        text: undefined,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        useHTML: true,
        headerFormat: '',
        pointFormatter: function (this: any) {
          const point = this;
          const item = point.sourceItem;
          let html = `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 4px 2px; min-width: 150px;">
              <div style="font-weight: 700; font-size: 13px; color: #0f172a; margin-bottom: 4px;">${point.name}</div>
              <div style="font-size: 12px; color: #334155; margin-bottom: 6px;">
                Jumlah: <b style="color: #7BAA18; font-size: 13px;">${(point.y || 0).toLocaleString()}</b> leads (${point.percentage ? point.percentage.toFixed(1) : 0}%)
              </div>`;

          if (item) {
            const hasBreakdown =
              item.hot_count !== undefined ||
              item.rsv_count !== undefined ||
              item.closing_ok_count !== undefined ||
              item.cold_count !== undefined;

            if (hasBreakdown) {
              html += `<div style="border-top: 1px solid #e2e8f0; padding-top: 5px; display: flex; flex-direction: column; gap: 3px; font-size: 11px;">`;
              if (item.closing_ok_count !== undefined && item.closing_ok_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #10b981; font-weight: 600;">🏆 Closing OK:</span> <b>${item.closing_ok_count}</b></div>`;
              }
              if (item.rsv_count !== undefined && item.rsv_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #f59e0b; font-weight: 600;">📋 Reservasi:</span> <b>${item.rsv_count}</b></div>`;
              }
              if (item.hot_count !== undefined && item.hot_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #ef4444; font-weight: 600;">🔥 Hot:</span> <b>${item.hot_count}</b></div>`;
              }
              if (item.cold_count !== undefined && item.cold_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #64748b; font-weight: 600;">❄️ Cold:</span> <b>${item.cold_count}</b></div>`;
              }
              if (item.conversion_rate !== undefined && item.conversion_rate > 0) {
                html += `<div style="display: flex; justify-content: space-between; margin-top: 2px; border-top: 1px dashed #e2e8f0; padding-top: 2px;"><span style="color: #0284c7; font-weight: 600;">⚡ Conv. Rate:</span> <b>${item.conversion_rate}%</b></div>`;
              }
              html += `</div>`;
            }
          }
          html += `</div>`;
          return html;
        },
      },
      plotOptions: {
        pie: {
          innerSize: '55%',
          allowPointSelect: true,
          cursor: 'pointer',
          showInLegend: false,
          colors: ['#7BAA18', '#1890ff', '#faad14', '#722ed1', '#13c2c2', '#eb2f96', '#f97316', '#64748b'],
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%',
            distance: 18,
            style: {
              color: '#334155',
              fontSize: '11px',
              fontWeight: '600',
              textOutline: 'none',
            },
          },
          events: {
            click: (event: any) => {
              const item = event.point.sourceItem;
              if (item) {
                handleSourceClick(item);
              }
            },
          },
        },
      },
      credits: { enabled: false },
      series: [
        {
          name: 'Jumlah Leads',
          data: chartData,
        },
      ],
    };
  }, [sortedList, handleSourceClick]);

  return (
    <div style={{ ...glassCardStyle, marginBottom: '24px' }} className="glass-card-interactive">
      {/* Header Kartu */}
      <div style={{ marginBottom: '20px' }}>
        <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
          Efektivitas Sumber Iklan &amp; Media Promosi
        </Title>
        <Text type="secondary" style={{ fontSize: '13px' }}>
          Analisis kontribusi channel perolehan leads, porsi efektivitas promosi, dan tingkat konversi closing.
        </Text>
      </div>

      {isCardLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : sortedList.length > 0 ? (
        /* Split View Layout Selaras: Donut Chart di Kiri (Col 15 = 2/3) & Channel Leaderboard di Kanan (Col 9 = 1/3) */
        <Row gutter={[20, 20]} align="stretch">
          <Col xs={24} lg={15} xl={15}>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.35)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                padding: '16px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {/* Header ringkasan metrik di atas Donut Chart */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                  padding: '0 4px',
                }}
              >
                <div>
                  <Text strong style={{ fontSize: '13px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Proporsi Perolehan Leads
                  </Text>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Persentase kontribusi per saluran promosi
                  </div>
                </div>

                <div
                  style={{
                    background: 'rgba(123, 170, 24, 0.1)',
                    borderRadius: '10px',
                    padding: '6px 14px',
                    border: '1px solid rgba(123, 170, 24, 0.25)',
                    textAlign: 'right',
                  }}
                >
                  <div style={{ fontSize: '11px', color: '#55790f', fontWeight: 600 }}>Total Promosi</div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    {totalLeads.toLocaleString()}{' '}
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#7BAA18' }}>Leads</span>
                  </div>
                </div>
              </div>

              {donutOptions && <HighchartsReact highcharts={Highcharts} options={donutOptions} />}
            </div>
          </Col>

          <Col xs={24} lg={9} xl={9}>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.03)',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: '440px',
              }}
            >
              {/* Header List */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                      color: '#15803d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                    }}
                  >
                    <TrophyOutlined />
                  </div>
                  <div>
                    <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>
                      Peringkat Media
                    </Text>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {sortedList.length} Saluran • {totalLeads.toLocaleString()} Leads
                    </div>
                  </div>
                </div>

                {sortedList[0] && (
                  <Tag
                    color="success"
                    style={{
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '1px 8px',
                      border: 'none',
                      background: 'rgba(123, 170, 24, 0.15)',
                      color: '#55790f',
                    }}
                  >
                    🏆 Top: {sortedList[0].source}
                  </Tag>
                )}
              </div>

              {/* Scrollable Channel List */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: '360px',
                  paddingRight: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {sortedList.map((item, index) => {
                  const rank = index + 1;
                  const percentage =
                    item.percentage !== undefined
                      ? item.percentage.toFixed(1)
                      : totalLeads > 0
                        ? ((item.count / totalLeads) * 100).toFixed(1)
                        : '0';

                  const progressRelative = Math.round(((item.count || 0) / maxCount) * 100);

                  // Gaya badge ranking
                  let rankBadgeStyle: React.CSSProperties = {
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 800,
                  };

                  if (rank === 1) {
                    rankBadgeStyle = {
                      ...rankBadgeStyle,
                      background: 'linear-gradient(135deg, #fef08a 0%, #facc15 100%)',
                      color: '#854d0e',
                      boxShadow: '0 2px 6px rgba(250, 204, 21, 0.35)',
                    };
                  } else if (rank === 2) {
                    rankBadgeStyle = {
                      ...rankBadgeStyle,
                      background: 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)',
                      color: '#334155',
                      boxShadow: '0 2px 6px rgba(148, 163, 184, 0.3)',
                    };
                  } else if (rank === 3) {
                    rankBadgeStyle = {
                      ...rankBadgeStyle,
                      background: 'linear-gradient(135deg, #ffedd5 0%, #fdba74 100%)',
                      color: '#9a3412',
                      boxShadow: '0 2px 6px rgba(251, 146, 60, 0.3)',
                    };
                  } else {
                    rankBadgeStyle = {
                      ...rankBadgeStyle,
                      background: 'rgba(241, 245, 249, 0.85)',
                      color: '#64748b',
                    };
                  }

                  const hasBreakdown =
                    item.cold_count !== undefined ||
                    item.hot_count !== undefined ||
                    item.rsv_count !== undefined ||
                    item.closing_ok_count !== undefined ||
                    item.conversion_rate !== undefined;

                  return (
                    <div
                      key={item.source_id || item.source}
                      onClick={() => handleSourceClick(item)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.6)',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        borderRadius: '12px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                      className="source-rank-item"
                    >
                      {/* Baris Atas: Rank, Ikon Platform, Nama, Jenis & Leads Count */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                          <div style={rankBadgeStyle}>#{rank}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {getPlatformIcon(item.source)}
                            <Text
                              ellipsis={{ tooltip: item.source }}
                              style={{
                                fontWeight: 700,
                                fontSize: '13px',
                                color: '#1e293b',
                              }}
                            >
                              {item.source}
                            </Text>
                          </div>
                          {item.source_type && (
                            <Tag
                              color={item.source_type.toLowerCase() === 'online' ? 'cyan' : 'purple'}
                              style={{
                                fontSize: '10px',
                                borderRadius: '4px',
                                border: 'none',
                                lineHeight: '18px',
                                padding: '0 5px',
                                textTransform: 'capitalize',
                              }}
                            >
                              {item.source_type}
                            </Tag>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                          <span style={{ fontSize: '13px', fontWeight: 800, color: '#7BAA18' }}>
                            {item.count.toLocaleString()}
                          </span>
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                            ({percentage}%)
                          </span>
                          <ArrowRightOutlined
                            style={{
                              fontSize: '11px',
                              color: '#94a3b8',
                              marginLeft: '2px',
                              transition: 'transform 0.2s ease',
                            }}
                          />
                        </div>
                      </div>

                      {/* Baris Tengah: Relative Progress Bar */}
                      <div style={{ marginTop: '4px' }}>
                        <Progress
                          percent={progressRelative}
                          showInfo={false}
                          size="small"
                          strokeColor={
                            rank === 1 ? '#7BAA18' : rank === 2 ? '#84cc16' : rank === 3 ? '#a3e635' : '#1890ff'
                          }
                          trailColor="rgba(0, 0, 0, 0.04)"
                          style={{ margin: 0 }}
                        />
                      </div>

                      {/* Baris Bawah: Tag Status Kualitas & Conversion Rate */}
                      {hasBreakdown && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                          {item.closing_ok_count !== undefined && item.closing_ok_count > 0 && (
                            <Tooltip title="Transaksi Closing Disetujui">
                              <Tag
                                style={{
                                  margin: 0,
                                  fontSize: '10px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'rgba(16, 185, 129, 0.12)',
                                  color: '#059669',
                                  fontWeight: 600,
                                  padding: '0 5px',
                                  lineHeight: '18px',
                                }}
                              >
                                🏆 {item.closing_ok_count} OK
                              </Tag>
                            </Tooltip>
                          )}
                          {item.rsv_count !== undefined && item.rsv_count > 0 && (
                            <Tooltip title="Reservasi Booking Unit">
                              <Tag
                                style={{
                                  margin: 0,
                                  fontSize: '10px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'rgba(245, 158, 11, 0.12)',
                                  color: '#d97706',
                                  fontWeight: 600,
                                  padding: '0 5px',
                                  lineHeight: '18px',
                                }}
                              >
                                📋 {item.rsv_count} RSV
                              </Tag>
                            </Tooltip>
                          )}
                          {item.hot_count !== undefined && item.hot_count > 0 && (
                            <Tooltip title="Prospek Hot">
                              <Tag
                                style={{
                                  margin: 0,
                                  fontSize: '10px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'rgba(239, 68, 68, 0.12)',
                                  color: '#dc2626',
                                  fontWeight: 600,
                                  padding: '0 5px',
                                  lineHeight: '18px',
                                }}
                              >
                                🔥 {item.hot_count} Hot
                              </Tag>
                            </Tooltip>
                          )}
                          {item.cold_count !== undefined && item.cold_count > 0 && (
                            <Tooltip title="Prospek Cold">
                              <Tag
                                style={{
                                  margin: 0,
                                  fontSize: '10px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'rgba(100, 116, 139, 0.12)',
                                  color: '#475569',
                                  fontWeight: 600,
                                  padding: '0 5px',
                                  lineHeight: '18px',
                                }}
                              >
                                ❄️ {item.cold_count} Cold
                              </Tag>
                            </Tooltip>
                          )}
                          {item.conversion_rate !== undefined && item.conversion_rate > 0 && (
                            <Tooltip title="Conversion Rate (Rasio Closing terhadap Leads)">
                              <Tag
                                style={{
                                  margin: 0,
                                  fontSize: '10px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'rgba(2, 132, 199, 0.12)',
                                  color: '#0284c7',
                                  fontWeight: 700,
                                  padding: '0 5px',
                                  lineHeight: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                }}
                              >
                                <ThunderboltOutlined style={{ fontSize: '9px' }} /> CR: {item.conversion_rate}%
                              </Tag>
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer List */}
              <div
                style={{
                  marginTop: '10px',
                  paddingTop: '8px',
                  borderTop: '1px solid rgba(226, 232, 240, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                <span>Klik channel untuk filter leads</span>
                <span style={{ fontWeight: 600, color: '#7BAA18' }}>Urut Berdasarkan Kontribusi ↓</span>
              </div>
            </div>
          </Col>
        </Row>
      ) : (
        <Empty description="Tidak ada data sumber leads" />
      )}
    </div>
  );
};

export default LeadsBySourceCard;
